import { $, bindClick } from "../lib/dom.js";
import { escHtml } from "../lib/hints.js";
import { api } from "../api.js";

const STATUS_LABELS = {
  pending_pay: "待付款",
  paid: "待发货",
  shipped: "待收货",
  completed: "已完成",
  cancelled: "已取消",
  refunded: "已退款",
  refunding: "退款处理中",
};

const TABS = [
  { key: "", label: "全部" },
  { key: "pending_pay", label: "待付款" },
  { key: "paid", label: "待发货" },
  { key: "shipped", label: "待收货" },
  { key: "after_sale", label: "售后待处理" },
  { key: "refunded", label: "已退款" },
];

let activeTab = "";
let selectedOrderId = "";
/** 详情面板当前这单；发货表单取消时要靠它把详情重画回来。 */
let currentDetailOrder = null;
let listPage = 1;
const PAGE_SIZE = 50;
/** 当前列表页缓存，发货/退款后用 PATCH 响应就地刷新，避免整表重拉。 */
let lastListItems = [];

function orderMatchesActiveTab(order) {
  const st = String(order.status || "").trim().toLowerCase();
  if (!activeTab) return true;
  if (activeTab === "after_sale") {
    return !!(
      order.has_after_sale ||
      (order.return_feedback && st !== "refunded" && st !== "cancelled")
    );
  }
  return st === activeTab;
}

function applyOrderFromApi(patched) {
  if (!patched || !patched.order_id) return;
  const oid = patched.order_id;
  const idx = lastListItems.findIndex((i) => i.order_id === oid);
  let merged = patched;
  if (idx >= 0) {
    merged = Object.assign({}, lastListItems[idx], patched);
  }
  if (orderMatchesActiveTab(merged)) {
    if (idx >= 0) lastListItems[idx] = merged;
    else lastListItems.unshift(merged);
  } else if (idx >= 0) {
    lastListItems.splice(idx, 1);
  }
  renderList(lastListItems);
  if (selectedOrderId === oid) {
    renderDetail(merged);
  }
}

function fmtMoney(cents) {
  return `¥${(Number(cents || 0) / 100).toFixed(2)}`;
}

function fmtTime(iso) {
  return String(iso || "").replace("T", " ").slice(0, 19);
}

function orderIdShort(id) {
  const s = String(id || "");
  if (s.length <= 24) return s;
  return `${s.slice(0, 10)}…${s.slice(-8)}`;
}

function statusBadge(status) {
  const st = String(status || "");
  const label = STATUS_LABELS[st] || st || "—";
  const cls = st ? ` mc-mo-badge--${escHtml(st)}` : "";
  return `<span class="mc-mo-badge${cls}">${escHtml(label)}</span>`;
}

function itemSummary(order) {
  const items = order.items || [];
  if (!items.length) return "—";
  const first = items[0];
  const title = first.title || first.num_iid || first.sku_id || "商品";
  const more = items.length > 1 ? ` 等${items.length}件` : "";
  return `${title}${more}`;
}

function addressText(order) {
  const geo = order.geo || {};
  const t = String(geo.address_text || "").trim();
  if (t) return t;
  return "（下单时未带回地址快照）";
}

function customerContact(order) {
  const c = order.customer || {};
  const name =
    String(c.consignee_name || c.display_name || "").trim() || "—";
  const phone =
    String(c.consignee_phone || c.account_phone || "").trim() || "";
  if (phone) return `${name} · ${phone}`;
  return name;
}

function customerDetailRows(order) {
  const c = order.customer || {};
  const rows = [];
  const consignee = String(c.consignee_name || "").trim();
  const consigneePhone = String(c.consignee_phone || "").trim();
  const display = String(c.display_name || "").trim();
  const accountPhone = String(c.account_phone || "").trim();
  const pid = String(c.person_id || order.person_id || "").trim();
  if (consignee) rows.push(["收货人", consignee]);
  if (consigneePhone) rows.push(["收货手机", consigneePhone]);
  if (display) rows.push(["顾客昵称", display]);
  if (accountPhone) rows.push(["账号手机", accountPhone]);
  if (pid) rows.push(["顾客 ID", pid]);
  if (!rows.length) {
    rows.push(["顾客", "（无联系信息快照，可查账号手机绑定）"]);
  }
  return rows
    .map(
      ([dt, dd]) =>
        `<dt>${escHtml(dt)}</dt><dd>${escHtml(dd)}</dd>`
    )
    .join("");
}

function buildQuery(page) {
  const pg = Math.max(1, Number(page) || 1);
  const parts = [`page=${pg}`, `limit=${PAGE_SIZE}`];
  if (activeTab === "after_sale") {
    parts.push("after_sale=1");
  } else if (activeTab) {
    parts.push(`status=${encodeURIComponent(activeTab)}`);
  }
  return parts.join("&");
}

function renderTabs() {
  const box = $("mall-orders-tabs");
  if (!box) return;
  box.innerHTML = TABS.map(
    (t) =>
      `<button type="button" class="${activeTab === t.key ? "active" : ""}" data-tab="${escHtml(t.key)}">${escHtml(t.label)}</button>`
  ).join("");
  box.querySelectorAll("[data-tab]").forEach((btn) => {
    btn.addEventListener("click", () => {
      activeTab = btn.getAttribute("data-tab") || "";
      listPage = 1;
      loadMallOrdersPanel();
    });
  });
}

function renderList(items) {
  const box = $("mall-orders-list");
  if (!box) return;
  if (!items.length) {
    box.innerHTML = '<p class="mc-mo-empty">暂无订单</p>';
    return;
  }
  const rows = items
    .map((o) => {
      const after = o.has_after_sale
        ? '<span class="mc-mo-after-tag">售后</span>'
        : "";
      const selected = selectedOrderId === o.order_id ? " mc-mo-row-selected" : "";
      return `<tr class="mc-mo-row${selected}" data-oid="${escHtml(o.order_id)}">
        <td><span class="mc-mo-oid" title="${escHtml(o.order_id)}">${escHtml(orderIdShort(o.order_id))}</span></td>
        <td>${statusBadge(o.status)}${after}</td>
        <td><strong>${escHtml(fmtMoney(o.total_amount_cents))}</strong></td>
        <td class="muted">${escHtml(fmtTime(o.created_at))}</td>
        <td class="mc-mo-contact" title="${escHtml(customerContact(o))}">${escHtml(customerContact(o))}</td>
        <td class="mc-mo-goods" title="${escHtml(itemSummary(o))}">${escHtml(itemSummary(o))}</td>
      </tr>`;
    })
    .join("");
  box.innerHTML = `<div class="mc-mo-table-wrap"><table class="mc-mo-table"><thead><tr>
    <th>订单号</th><th>状态</th><th>金额</th><th>下单时间</th><th>客户/联系</th><th>商品</th>
  </tr></thead><tbody>${rows}</tbody></table></div>`;
  box.querySelectorAll(".mc-mo-row").forEach((tr) => {
    tr.addEventListener("click", () => {
      const oid = tr.getAttribute("data-oid");
      if (oid) openDetail(oid);
    });
  });
}

function renderDetail(order) {
  const box = $("mall-orders-detail");
  const actions = $("mall-orders-actions");
  if (!box || !actions) return;
  currentDetailOrder = order && order.order_id ? order : null;
  if (!order) {
    box.innerHTML = '<p class="mc-mo-empty">选择左侧订单查看详情</p>';
    actions.innerHTML = "";
    return;
  }
  const st = String(order.status || "").trim().toLowerCase();
  const fb = order.return_feedback;
  let fbHtml = "";
  if (fb) {
    fbHtml = `<div class="mc-mo-callout">
      <strong>售后申请</strong><br/>
      原因码：${escHtml(fb.return_reason_code || "")}<br/>
      说明：${escHtml(fb.return_reason_text || "—")}<br/>
      登记时间：${escHtml(fmtTime(fb.created_at))}
    </div>`;
  }
  const lines = (order.items || [])
    .map(
      (it) =>
        `<li>${escHtml(it.title || it.sku_id || it.num_iid || "SKU")} × ${Number(it.quantity || 1)}</li>`
    )
    .join("");
  box.innerHTML = `
    <h3>订单详情</h3>
    <p class="mc-mo-oid" style="margin:0 0 8px">${escHtml(order.order_id)}</p>
    <dl class="mc-mo-meta">
      <dt>状态</dt><dd>${statusBadge(st)}</dd>
      <dt>金额</dt><dd><strong>${escHtml(fmtMoney(order.total_amount_cents))}</strong></dd>
      <dt>下单时间</dt><dd>${escHtml(fmtTime(order.created_at))}</dd>
    </dl>
    <h4>客户信息</h4>
    <dl class="mc-mo-meta">${customerDetailRows(order)}</dl>
    <h4>收货地址</h4>
    <p class="mc-mo-address">${escHtml(addressText(order))}</p>
    ${fbHtml}
    <h4>商品明细</h4>
    <ul>${lines || "<li>—</li>"}</ul>
  `;

  const hasAfterSale =
    order.has_after_sale ||
    (fb && st !== "refunded" && st !== "cancelled");
  const canRefund =
    hasAfterSale && (st === "paid" || st === "shipped" || st === "completed");

  const btns = [];
  if (st === "paid") {
    btns.push('<button type="button" id="btn-mo-ship">标记发货</button>');
  }
  if (canRefund) {
    btns.push(
      '<button type="button" class="warn mc-mo-btn-refund" id="btn-mo-refund">同意退货退款</button>'
    );
  }
  if (st === "refunding") {
    btns.push(
      '<p class="mc-mo-action-hint">已向微信发起原路退款，处理中；成功后订单将变为已退款。</p>'
    );
  } else if (canRefund) {
    btns.push(
      '<p class="mc-mo-action-hint">确认后将调用微信支付退款接口，货款原路退回买家（mock 环境即时成功）。</p>'
    );
  }
  actions.innerHTML = btns.join(" ");
  bindClick("btn-mo-ship", () => shipOrder(order));
  bindClick("btn-mo-refund", () => refundOrder(order));
}

async function openDetail(orderId) {
  selectedOrderId = orderId;
  const listBox = $("mall-orders-list");
  if (listBox) {
    listBox.querySelectorAll(".mc-mo-row").forEach((tr) => {
      tr.classList.toggle("mc-mo-row-selected", tr.getAttribute("data-oid") === orderId);
    });
  }
  const box = $("mall-orders-detail");
  if (box) box.innerHTML = '<p class="mc-mo-empty">加载中…</p>';
  const { ok, data } = await api(
    "GET",
    `/api/v1/fitting/merchant/mall/orders/${encodeURIComponent(orderId)}`
  );
  if (!ok) {
    if (box) box.innerHTML = '<p class="mc-mo-empty">加载失败</p>';
    return;
  }
  renderDetail(data);
}

/** 运力表兜底：后端 `GET /fitting/mall/delivery-companies` 本身已有兜底，
 *  这里只兜「接口都没打通」的网络态，保证下拉不会空着。 */
const DELIVERY_FALLBACK = [
  { code: "SF", name: "顺丰速运" },
  { code: "ZTO", name: "中通快递" },
  { code: "YTO", name: "圆通速递" },
  { code: "STO", name: "申通快递" },
  { code: "YD", name: "韵达速递" },
  { code: "JTSD", name: "极兔速递" },
];

/** 常用国内运力，置顶成一组。微信 `get_delivery_list` 有 1500+ 条（含大量国际
 *  件），整坨平铺找不到人；常用的排前面，其余归「全部运力」。编码与后端
 *  `_DELIVERY_COMPANY_FALLBACK` 一致。 */
const DELIVERY_COMMON = ["SF", "ZTO", "YTO", "STO", "YD", "JD", "JTSD", "EMS", "DBL", "YZPY"];

let deliveryCompaniesCache = null;

async function loadDeliveryCompanies() {
  if (deliveryCompaniesCache) return deliveryCompaniesCache;
  const { ok, data } = await api("GET", "/api/v1/fitting/mall/delivery-companies");
  const rows = ok && data && Array.isArray(data.companies) ? data.companies : [];
  const list = rows.filter((r) => r && r.code && r.name);
  deliveryCompaniesCache = list.length ? list : DELIVERY_FALLBACK;
  return deliveryCompaniesCache;
}

/** 发货表单：快递必须选公司 + 填单号；自提要显式选。
 *
 * 微信「发货信息管理」要求 `express_company` 是**运力编码**（STO/SF/YTO），
 * 手填中文名会被拒；也**不允许**「没单号就默认自提」蒙混 —— 故这里不再用
 * `window.prompt`，改成结构化表单，且把后端的 400 原样显示出来。 */
async function shipOrder(order) {
  const orderId = order && order.order_id;
  const actions = $("mall-orders-actions");
  if (!orderId || !actions) return;
  actions.innerHTML = `
    <div class="mc-mo-ship-form">
      <label class="mc-mo-ship-field">发货方式
        <select id="mo-ship-mode">
          <option value="1">快递</option>
          <option value="4">门店自提</option>
        </select>
      </label>
      <label class="mc-mo-ship-field" id="mo-ship-company-wrap">快递公司
        <select id="mo-ship-company"><option value="">加载中…</option></select>
      </label>
      <label class="mc-mo-ship-field" id="mo-ship-tracking-wrap">运单号
        <input id="mo-ship-tracking" type="text" autocomplete="off" placeholder="快递面单号" />
      </label>
      <button type="button" id="btn-mo-ship-submit">确认发货</button>
      <button type="button" id="btn-mo-ship-cancel">取消</button>
      <p class="mc-mo-action-hint" id="mo-ship-err"></p>
    </div>`;
  const modeEl = $("mo-ship-mode");
  const companyWrap = $("mo-ship-company-wrap");
  const trackingWrap = $("mo-ship-tracking-wrap");
  const errEl = $("mo-ship-err");
  const toggleMode = () => {
    const isExpress = modeEl.value === "1";
    companyWrap.classList.toggle("hidden", !isExpress);
    trackingWrap.classList.toggle("hidden", !isExpress);
    if (errEl) errEl.textContent = "";
  };
  modeEl.addEventListener("change", toggleMode);
  toggleMode();

  const companies = await loadDeliveryCompanies();
  const companyEl = $("mo-ship-company");
  if (companyEl) {
    const byCode = new Map(companies.map((c) => [c.code, c]));
    const option = (c) =>
      `<option value="${escHtml(c.code)}">${escHtml(c.name)}</option>`;
    const common = DELIVERY_COMMON.map((code) => byCode.get(code)).filter(Boolean);
    const commonCodes = new Set(common.map((c) => c.code));
    const rest = companies.filter((c) => !commonCodes.has(c.code));
    companyEl.innerHTML =
      `<optgroup label="常用">${common.map(option).join("")}</optgroup>` +
      (rest.length ? `<optgroup label="全部运力">${rest.map(option).join("")}</optgroup>` : "");
  }

  bindClick("btn-mo-ship-cancel", () => renderDetail(currentDetailOrder));
  bindClick("btn-mo-ship-submit", async () => {
    const logisticsType = Number(modeEl.value);
    const tracking = ($("mo-ship-tracking") && $("mo-ship-tracking").value.trim()) || "";
    const company = ($("mo-ship-company") && $("mo-ship-company").value.trim()) || "";
    if (logisticsType === 1 && (!tracking || !company)) {
      errEl.textContent = "快递发货须填写快递公司与运单号；门店自提请选「门店自提」。";
      return;
    }
    const body = { status: "shipped", logistics_type: logisticsType };
    if (logisticsType === 1) {
      body.tracking_no = tracking;
      body.express_company = company;
    }
    const { ok, data } = await api(
      "PATCH",
      `/api/v1/fitting/orders/${encodeURIComponent(orderId)}/status`,
      { body }
    );
    if (!ok) {
      errEl.textContent = errorMessage(data);
      return;
    }
    applyOrderFromApi(
      data && data.order_id ? data : Object.assign({ order_id: orderId }, body)
    );
  });
}

/** 把后端错误摊平成一句话（`{error:{code,message}}` / `{message}` / `{detail}` 三种都见得到）。 */
function errorMessage(data) {
  if (!data) return "发货失败";
  const e = data.error;
  if (e && typeof e === "object") {
    return [e.code, e.message].filter(Boolean).join("：") || "发货失败";
  }
  if (typeof e === "string" && e) return e;
  return String(data.message || data.detail || "发货失败");
}

async function refundOrder(order) {
  const orderId = order && order.order_id;
  if (!orderId) return;
  const remain = Math.max(
    0,
    Number(order.total_amount_cents || 0) -
      Number((order.payment_extra && order.payment_extra.refund && order.payment_extra.refund.refunded_cents_total) || 0)
  );
  const partialRaw = window.prompt(
    `退款金额（分，留空=退剩余 ${remain} 分）`,
    remain ? String(remain) : ""
  );
  if (partialRaw === null) return;
  let refundAmountCents;
  if (String(partialRaw).trim()) {
    refundAmountCents = Number(String(partialRaw).trim());
    if (!Number.isFinite(refundAmountCents) || refundAmountCents <= 0) {
      window.alert("请输入有效退款金额（分）");
      return;
    }
  }
  if (
    !window.confirm(
      "确认退款？将通过微信支付原路退回（处理中请稍后刷新订单状态）。"
    )
  ) {
    return;
  }
  const fb = order.return_feedback || {};
  const detail = $("mall-orders-detail");
  const { ok, data } = await api(
    "PATCH",
    `/api/v1/fitting/orders/${encodeURIComponent(orderId)}/status`,
    {
      body: {
        status: "refunded",
        refund_amount_cents: refundAmountCents,
        return_reason_code: fb.return_reason_code || undefined,
        return_reason_text: fb.return_reason_text || undefined,
      },
    }
  );
  if (ok) {
    applyOrderFromApi(data && data.order_id ? data : { order_id: orderId, status: "refunding" });
  } else if (detail) {
    detail.insertAdjacentHTML("beforeend", '<p class="mc-desc">退款失败，请查看接口报错或支付配置。</p>');
  }
}

function renderPager(data) {
  const box = $("mall-orders-pager");
  if (!box) return;
  const total = Number((data && data.total) || 0);
  const page = Number((data && data.page) || listPage);
  const size = Number((data && data.size) || PAGE_SIZE);
  const pages = Math.max(1, Math.ceil(total / size));
  if (total <= size) {
    box.innerHTML = total
      ? `<span class="muted">共 ${total} 单</span>`
      : "";
    return;
  }
  box.innerHTML = `
    <button type="button" id="btn-mo-prev" ${page <= 1 ? "disabled" : ""}>上一页</button>
    <span class="muted">第 ${page} / ${pages} 页 · 共 ${total} 单</span>
    <button type="button" id="btn-mo-next" ${!data.has_more ? "disabled" : ""}>下一页</button>
  `;
  bindClick("btn-mo-prev", () => {
    if (listPage <= 1) return;
    listPage -= 1;
    loadMallOrdersPanel();
  });
  bindClick("btn-mo-next", () => {
    if (!data || !data.has_more) return;
    listPage += 1;
    loadMallOrdersPanel();
  });
}

export async function loadMallOrdersPanel() {
  renderTabs();
  const listBox = $("mall-orders-list");
  if (listBox) listBox.innerHTML = '<p class="mc-mo-empty">加载中…</p>';
  const { ok, data } = await api(
    "GET",
    `/api/v1/fitting/merchant/mall/orders?${buildQuery(listPage)}`
  );
  if (!ok) {
    if (listBox) listBox.innerHTML = '<p class="mc-mo-empty">加载失败</p>';
    renderPager(null);
    return;
  }
  listPage = Number(data.page) || listPage;
  const items = (data && data.items) || [];
  lastListItems = items;
  renderList(items);
  renderPager(data);
  if (selectedOrderId && items.some((i) => i.order_id === selectedOrderId)) {
    await openDetail(selectedOrderId);
  } else {
    selectedOrderId = "";
    renderDetail(null);
  }
}

export function initMallOrdersHandlers() {
  bindClick("btn-mall-orders-refresh", () => loadMallOrdersPanel());
}
